import { useEffect, useState, useCallback } from 'react';

import copy from 'copy-to-clipboard';

type TextToCopy = string | number;
type HookReturnType = { isCopied: boolean; handleCopy: (text: TextToCopy) => void };

/**
 * Copy a string or a number to clipboard
 * @param number resetInterval - Interval of time in miliseconds to reset the isCopied state
 * @returns Object containing the state and the handler to copy to clipboard
 */
const useCopyToClipboard = (resetInterval: number | null = null): HookReturnType => {
    const [isCopied, setCopied] = useState<boolean>(false);

    const handleCopy = useCallback((text: TextToCopy): void => {
        if (typeof text === 'string' || typeof text === 'number') {
            copy(text.toString());
            setCopied(true);
        } else {
            setCopied(false);
        }
    }, []);

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        if (isCopied && resetInterval) {
            // isCopied is reset after interval timeout
            timeout = setTimeout(() => setCopied(false), resetInterval);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [isCopied, resetInterval]);
    return { isCopied, handleCopy };
};

export default useCopyToClipboard;
